import { Breadcrumbs } from "@admin-ui/components/application/breadcrumbs/breadcrumbs";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { ArrowLeft } from "@untitledui/icons";
import type { FC, ReactNode } from "react";

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: FC<{ className?: string }>;
}

export interface AdminPageHeaderProps {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  /** Inline content rendered next to the title — typically a count Badge. */
  titleBadge?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Mobile back button href. Defaults to the first breadcrumb's href. */
  backHref?: string;
}

export function AdminPageHeader({ breadcrumbs, title, titleBadge, subtitle, actions, backHref }: AdminPageHeaderProps) {
  const resolvedBackHref = backHref ?? breadcrumbs[0]?.href;

  return (
    <div className="mx-auto mb-8 flex w-full max-w-container flex-col gap-5 px-4 lg:px-8">
      <div className="flex flex-col gap-4">
        {breadcrumbs.length > 0 && (
          <div className="max-lg:hidden">
            <Breadcrumbs type="button">
              {breadcrumbs.map((item) => (
                <Breadcrumbs.Item key={item.href ?? `current:${item.label}`} href={item.href} icon={item.icon}>
                  {item.label}
                </Breadcrumbs.Item>
              ))}
            </Breadcrumbs>
          </div>
        )}
        {resolvedBackHref && (
          <div className="flex lg:hidden">
            <Button href={resolvedBackHref} color="link-gray" size="xs" iconLeading={ArrowLeft}>
              Back
            </Button>
          </div>
        )}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold text-primary">{title}</h1>
              {titleBadge}
            </div>
            {subtitle && <div className="text-sm text-tertiary">{subtitle}</div>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
